import { useEffect, useRef } from "react"

interface SafariTopBarTintProps {
  boundaryId: string
  handoffOffsetPx: number
}

export function SafariTopBarTint({ boundaryId, handoffOffsetPx }: SafariTopBarTintProps) {
  const tintRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const tint = tintRef.current
    if (!tint) return

    let boundary: HTMLElement | null = null
    let frame: number | null = null
    let wasPastBoundary: boolean | undefined

    const syncTintToScrollPosition = () => {
      frame = null

      // Cache the route-owned boundary, but resolve it again if navigation
      // replaces the hero without remounting the shared site header.
      if (!boundary?.isConnected) boundary = document.getElementById(boundaryId)
      if (!boundary) return

      const isPastBoundary = boundary.getBoundingClientRect().bottom <= -handoffOffsetPx
      if (isPastBoundary === wasPastBoundary) return
      wasPastBoundary = isPastBoundary

      // Past the hero, stop overriding Safari so its native light/dark
      // scroll-edge gradient can follow the page underneath.
      tint.hidden = isPastBoundary
    }

    const scheduleSync = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(syncTintToScrollPosition)
    }

    syncTintToScrollPosition()
    window.addEventListener("scroll", scheduleSync, { passive: true })
    window.addEventListener("resize", scheduleSync)
    return () => {
      window.removeEventListener("scroll", scheduleSync)
      window.removeEventListener("resize", scheduleSync)
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [boundaryId, handoffOffsetPx])

  return (
    // Safari extends a solid color from fixed viewport-edge elements into its
    // obscured browser chrome. Its current sampler ignores boxes 10px or
    // thinner, so h-3 provides a 12px candidate. The filter makes the box
    // visually transparent without setting the CSS opacity that WebKit skips.
    // https://bugs.webkit.org/show_bug.cgi?id=301756#c2
    // https://github.com/WebKit/WebKit/blob/main/Source/WebCore/page/LocalFrameView.cpp
    <div
      ref={tintRef}
      aria-hidden="true"
      data-safari-top-tint
      className="pointer-events-none fixed inset-x-0 top-0 z-30 h-3 bg-[#384674] md:hidden"
      style={{ filter: "opacity(0)" }}
    />
  )
}
